import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, FormHelperText, Switch, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { useStationsFilter } from '@/hooks/useStationsFilter';

import { SimpleError } from '@/components/formik/error/SimpleError';
import { FormikBoolean } from '@/components/formik/FormikBoolean';
import { FormikMultiSelect } from '@/components/formik/FormikMultiSelect';
import { styles } from '@/components/memberActions/MemberModal.styles';

import type { TEditMemberForm } from '@/types/member';

export const MemberRoles = () => {
  const { t } = useTranslation();
  const { lookupStations } = useStationsFilter('');
  const { values, errors, submitCount, setFieldValue } = useFormikContext<TEditMemberForm>();

  const switchProps = (title: string, details: string) => ({
    control: <Switch />,
    labelPlacement: 'start' as const,
    label: (
      <Box>
        <Typography fontWeight={500}>{t(title)}</Typography>
        <Typography variant="body2" color="text.secondary" whiteSpace="pre-line">
          {t(details)}
        </Typography>
      </Box>
    ),
    disableTypography: true,
    fieldProps: { sx: styles.switchField },
    sx: styles.switchLabel,
  });

  const selectOptions = () => ({
    getOptions: lookupStations,
    label: t('members.userForm.assignToStations'),
    placeholder: t('members.userForm.selectStations'),
    searchPlaceholder: t('filtering.searchSites'),
    selectedTitle: t('members.userForm.selectedStations'),
  });

  useEffect(() => {
    if (!values.admin) {
      setFieldValue('adminAt', []);
    }

    if (!values.member) {
      setFieldValue('memberAt', []);
    }
  }, [values.admin, values.member, setFieldValue]);

  return (
    <>
      <FormikBoolean
        name="admin"
        {...switchProps('members.roles.admin', 'members.userForm.adminDetails')}
      />

      {values.admin && <FormikMultiSelect name="adminAt" {...selectOptions()} />}

      <FormikBoolean
        name="member"
        {...switchProps('members.roles.member', 'members.userForm.memberDetails')}
      />

      {values.member && <FormikMultiSelect name="memberAt" {...selectOptions()} />}

      {errors.member && submitCount > 0 && (
        <FormHelperText component="div" sx={styles.permissionsError}>
          <SimpleError error={errors.member} />
        </FormHelperText>
      )}
    </>
  );
};
